import { type ColumnId } from '@blaze-shared/api-contracts-domain';
import { type ChartVariantType } from '@blaze-shared/elements-domain';

import {
  type ChartPropsModel,
  type DATE_FORMAT_OPTIONS_VALUES,
  type TIME_FORMAT_OPTIONS_VALUES,
} from '../chart.models';

import { type ComputeKeys } from './chart-data.utils';

export const DATE_FORMAT_OPTIONS_VALUES_FORMAT_MAP: Record<
  DATE_FORMAT_OPTIONS_VALUES | TIME_FORMAT_OPTIONS_VALUES,
  [string, Intl.DateTimeFormatOptions]
> = {
  'number-month-first': ['en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }],
  'number-day-first': ['en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }],
  'short-month': ['en-US', { year: 'numeric', month: 'short', day: '2-digit' }],
  'long-month': ['en-US', { year: 'numeric', month: 'long', day: '2-digit' }],
  'number-month-first-year': ['en-US', { year: 'numeric', month: '2-digit' }],
  'short-month-year': ['en-US', { year: 'numeric', month: 'short' }],
  'long-month-year': ['en-US', { year: 'numeric', month: 'long' }],
  'short-month-only': ['en-US', { month: 'short' }],
  'long-month-only': ['en-US', { month: 'long' }],
  'with-meridiem': ['en-US', { hour: '2-digit', minute: '2-digit', hour12: true }],
  '24-hour': ['en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }],
};
export const EMPTY_DATE_LABEL = 'no date';

export function getDateFormatParts(
  contentFormat: ChartPropsModel['content']['format'],
  isSameYear?: boolean,
  axis: 'x' | 'series' = 'x',
): [string, Intl.DateTimeFormatOptions] {
  const pickedFormat = axis === 'x' ? contentFormat?.dateXFormat : contentFormat?.dateSeriesFormat;
  let intlDateFormatParts = pickedFormat && DATE_FORMAT_OPTIONS_VALUES_FORMAT_MAP[pickedFormat];
  if (
    isSameYear &&
    ['short-month-year', 'long-month-year'].includes(contentFormat?.dateXFormat || '')
  ) {
    switch (contentFormat?.dateXFormat) {
      case 'short-month-year':
        intlDateFormatParts = DATE_FORMAT_OPTIONS_VALUES_FORMAT_MAP['short-month-only'];
        break;
      case 'long-month-year':
        intlDateFormatParts = DATE_FORMAT_OPTIONS_VALUES_FORMAT_MAP['long-month-only'];
    }
  }
  return intlDateFormatParts || DATE_FORMAT_OPTIONS_VALUES_FORMAT_MAP['number-month-first'];
}

export function parseLabelToStringCompare(
  row: any,
  sortKey: string,
  dataDetailColumn?: boolean,
): string {
  return dataDetailColumn && row[sortKey].split
    ? row[sortKey]
        .toString()
        .split(' ')
        .map((v: number) => (v < 10 ? '0' + v : v))
        .reverse()
        .join('')
    : row[sortKey].toString();
}

export function compareXAxis(
  a: any,
  b: any,
  sortKey: string,
  ascending: boolean,
  dataDetailColumn?: boolean,
): number {
  if (a.label === EMPTY_DATE_LABEL) {
    return -1;
  } else if (b.label === EMPTY_DATE_LABEL) {
    return 1;
  } else if (a[sortKey] && b[sortKey] && (isNaN(a[sortKey]) || isNaN(b[sortKey]))) {
    const aValue = parseLabelToStringCompare(a, sortKey, dataDetailColumn);
    const bValue = parseLabelToStringCompare(b, sortKey, dataDetailColumn);
    return ascending
      ? aValue.localeCompare(bValue, 'en', { numeric: true })
      : bValue.localeCompare(aValue, 'en', { numeric: true });
  } else {
    return ascending ? a[sortKey] - b[sortKey] : b[sortKey] - a[sortKey];
  }
}

function getDisplayValueFromLink(val: string | { display?: string }): string {
  return (
    val && typeof val === 'object' && val.display ? val.display.toString() : (val ?? '')
  ).toString();
}

export function getLabelForChart(
  groupByName = '',
  groupById: ColumnId = '' as ColumnId,
  value: any,
  formatter: Intl.DateTimeFormat,
  elementType: ChartVariantType,
  groupByDetailsName?: string,
  computeKeys: ComputeKeys = {
    year: 'computeByYear',
    month: 'computeByMonth',
    details: 'computeByDetails',
  },
): {
  label: string | string[];
  valueToSort: string | string[];
} {
  let label: string | string[];
  if (groupByName) {
    if (Array.isArray(value[groupByName])) {
      label = value[groupByName].map(getDisplayValueFromLink);
    } else {
      label = getDisplayValueFromLink(value[groupByName]);
    }
  } else {
    if (Array.isArray(value[groupById])) {
      label = value[groupById].map(getDisplayValueFromLink);
    } else {
      label =
        getDisplayValueFromLink(value[groupById]) ||
        (['chartPie', 'chartDoughnut'].includes(elementType) ? 'No Data' : '');
    }
  }

  let valueToSort: string | string[] =
    `${value[computeKeys.year]}/${value[computeKeys.month]}/${value[computeKeys.details]}`;

  if (
    groupByDetailsName === 'daily' &&
    [computeKeys.year, computeKeys.month, computeKeys.details].every(
      (key) => key in value && typeof value[key] === 'number',
    )
  ) {
    label = formatter?.format(
      new Date(value[computeKeys.year], value[computeKeys.month] - 1, value[computeKeys.details]),
    );
  } else if (
    groupByDetailsName === 'yearly' &&
    [computeKeys.year].every((key) => key in value && typeof value[key] === 'number')
  ) {
    label = value[computeKeys.year];
  } else if (
    groupByDetailsName === 'quarterly' &&
    [computeKeys.details, computeKeys.year].every(
      (key) => key in value && typeof value[key] === 'number',
    )
  ) {
    label = `Q${value[computeKeys.details]} ${value[computeKeys.year]}`;
  } else if (
    groupByDetailsName === 'monthly' &&
    [computeKeys.details, computeKeys.year].every(
      (key) => key in value && typeof value[key] === 'number',
    )
  ) {
    label = formatter?.format(new Date(value[computeKeys.year], value[computeKeys.details] - 1, 1));
  } else if (groupByDetailsName) {
    if (typeof value[computeKeys.details] === 'string') {
      const dateFragments = value[computeKeys.details]?.split?.('T')?.[0]?.split('-').map(Number);
      label = formatter?.format(new Date(dateFragments[0], dateFragments[1] - 1, dateFragments[2]));
      valueToSort = `${dateFragments[0]}/${dateFragments[1]}/${dateFragments[2]}`;
    } else if (
      [computeKeys.year, computeKeys.month, computeKeys.details].every(
        (key) => key in value && typeof value[key] === 'number',
      )
    ) {
      label = formatter?.format(
        new Date(value[computeKeys.year], value[computeKeys.month] - 1, value[computeKeys.details]),
      );
    }
  } else if (isTimeOnlyFormatter(formatter)) {
    valueToSort = label;
    if (formatter && typeof label === 'string') {
      const timeDate = parseTimeStringToDate(label);
      if (timeDate) {
        label = formatter.format(timeDate);
      }
    }
  } else {
    valueToSort = label;
  }
  return { label, valueToSort };
}

export function isTimeOnlyFormatter(formatter: Intl.DateTimeFormat): boolean {
  const opts = formatter.resolvedOptions();
  return !!opts.hour && !opts.year && !opts.day && !opts.month;
}

function parseTimeStringToDate(timeStr?: string): Date | null {
  const parts = timeStr?.split?.(':').map(Number);
  if (!parts || parts.length < 2 || parts.some(isNaN)) {
    return null;
  }
  const dateTime = new Date(1970, 0, 1, parts[0], parts[1], parts[2] ?? 0);
  return isNaN(dateTime.getTime()) ? null : dateTime;
}

export function splitTimeToParts(
  timeStr?: string,
): { hours: number; minutes: number; seconds: number } | null {
  const parts = timeStr?.split?.(':').map(Number);
  if (!parts || parts.length < 2 || parts.some(Number.isNaN)) {
    return null;
  }
  return { hours: parts[0], minutes: parts[1], seconds: parts[2] ?? 0 };
}
